import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { KEYS } from '../../../../constants'
import { getSuppression } from '../../../../utils/globalSuppression'
import getViewPosition from './getViewPositions'

export const DashboardNavigationContext = createContext({})

class NavigationManager {
  constructor (data, actions) {
    this.data = data
    this.actions = actions
  }

  getViewPosition (key) {
    if (!this.data) {
      return { top: 0, left: 0 }
    }
    return this.data.find(x => x.key === key)?.position ?? [0, 0]
  }

  getMiniPosition (key) {
    if (!this.data) {
      return { top: 0, left: 0 }
    }
    return this.data.find(x => x.key === key)?.miniPosition
  }

  getNextItem (path) {
    return this.data.find(x => path.includes(`/${x.path}`)).next
  }

  getPrevItem (path) {
    return this.data.find(x => path.includes(`/${x.path}`)).prev
  }

  goHome () {
    this.actions.goHome()
  }
}

export const useNavigationManager = (dashboardItems, root) => {
  const history = useHistory()
  const { pathname } = useLocation()

  const goHome = useCallback(() => {
    history.push(root)
  }, [history, root])
  const valuesManager = useMemo(() => {
    const items = dashboardItems.filter(item => !item.hidden)
    const count = items.length
    const results = items.map((item, index) => {
      return {
        ...item,
        position: getViewPosition(count, index),
        miniPosition: getViewPosition(count, index, false)
      }
    })
    const linkedResults = results.map((item, index) => {
      if (results.length === 1) {
        return {
          ...item,
          next: item.path,
          prev: item.path
        }
      }
      if (index === 0) {
        return {
          ...item,
          next: results.at(index + 1).path,
          prev: results.at(-1).path
        }
      }
      if (index === results.length - 1) {
        return {
          ...item,
          next: results.at(0).path,
          prev: results.at(index - 1).path
        }
      }
      return {
        ...item,
        next: results.at(index + 1).path,
        prev: results.at(index - 1).path
      }
    })

    const actions = {
      goHome
    }

    return new NavigationManager(linkedResults, actions)
  }, [dashboardItems, goHome])

  const keyHandler = useCallback((evt) => {
    if (getSuppression('wheel')) return

    let isEscape = false
    if ('key' in evt) {
      isEscape = evt.key === 'Escape' || evt.key === 'Esc'
    } else {
      isEscape = evt.keyCode === KEYS.ESC
    }
    const isRoot = pathname === root
    if (!isRoot && isEscape) {
      evt.stopPropagation()
      evt.preventDefault()
      history.push(root)
    }
    if (!isRoot && evt.key === 'ArrowLeft') {
      history.push(valuesManager.getPrevItem(pathname))
    }
    if (!isRoot && evt.key === 'ArrowRight') {
      history.push(valuesManager.getNextItem(pathname))
    }
  }, [history, pathname, root, valuesManager])

  useEffect(() => {
    window.addEventListener('keydown', keyHandler)
    return () => {
      window.removeEventListener('keydown', keyHandler)
    }
  }, [keyHandler])

  return valuesManager
}

export const useDashboardNavigation = () => {
  return useContext(DashboardNavigationContext)
}
